/**
 * Opening and closing the drawer.
 *
 * A shift is the unit the till is reconciled in: it starts with a counted float, collects
 * every sale rung up on that terminal, and ends with a second count. The variance between
 * what the drawer should hold and what it does is the number this screen exists to
 * produce -- so the count is required, and it is entered before the total it will be
 * compared against is shown.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LockKeyhole, ReceiptText } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { QueryState } from '@/components/QueryState'
import {
  closeShift,
  formatMoney,
  getShiftSummary,
  listSales,
  listShifts,
  listTerminals,
  openShift,
  rememberTerminal,
  rememberedTerminal,
  type ShiftSummary,
} from '@/features/pos/api'
import { fromCents, toCents } from '@/features/pos/cart'
import { toast } from '@/lib/toast'

export function PosShift() {
  const queryClient = useQueryClient()
  const [terminalId, setTerminalId] = useState<string | null>(rememberedTerminal)

  const terminals = useQuery({ queryKey: ['pos', 'terminals'], queryFn: () => listTerminals() })
  const terminal = terminals.data?.find((row) => row.id === terminalId) ?? null

  useEffect(() => {
    if (terminalId || !terminals.data?.length) return
    const active = terminals.data.filter((row) => row.status === 'active')
    if (active.length >= 1) setTerminalId(active[0].id)
  }, [terminals.data, terminalId])

  useEffect(() => rememberTerminal(terminalId), [terminalId])

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pos'] })

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Shift</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The drawer, from the float it opened with to the count it closes on.
          </p>
        </div>
        <Link
          to="/pos"
          className="text-sm text-brand-600 hover:underline dark:text-brand-400"
        >
          Back to the till
        </Link>
      </header>

      <QueryState isLoading={terminals.isLoading} error={terminals.error}>
        {(terminals.data?.length ?? 0) > 1 && (
          <Field label="Till">
            {(props) => (
              <select
                {...props}
                value={terminalId ?? ''}
                onChange={(event) => setTerminalId(event.target.value)}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                {(terminals.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name} · {row.code}
                    {row.open_shift_id ? ' (open)' : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {terminal == null ? (
          <Alert tone="info" title="No till at this location">
            A terminal has to be registered against the store before a shift can be opened on
            it.
          </Alert>
        ) : terminal.open_shift_id ? (
          <OpenShift shiftId={terminal.open_shift_id} onClosed={refresh} />
        ) : (
          <OpenTheDrawer terminalId={terminal.id} terminalName={terminal.name} onOpened={refresh} />
        )}
      </QueryState>

      {terminal && <ShiftHistory terminalId={terminal.id} />}
    </div>
  )
}

function OpenTheDrawer({
  terminalId,
  terminalName,
  onOpened,
}: {
  terminalId: string
  terminalName: string
  onOpened: () => void
}) {
  const [float, setFloat] = useState('')
  const [notes, setNotes] = useState('')

  const open = useMutation({
    mutationFn: () =>
      openShift({
        terminal_id: terminalId,
        opening_float: fromCents(toCents(float)),
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(`${terminalName} is open.`)
      onOpened()
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not open the shift.'),
  })

  return (
    <Card>
      <CardHeader
        title={`Open ${terminalName}`}
        description="Count the drawer before the first sale. Zero is a fair answer on a card-only till."
      />
      <CardBody>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            open.mutate()
          }}
        >
          <Field label="Opening float" hint="What is in the drawer now.">
            {(props) => (
              <Input
                {...props}
                autoFocus
                inputMode="decimal"
                placeholder="0.00"
                className="text-right tabular-nums"
                value={float}
                onChange={(event) => setFloat(event.target.value)}
              />
            )}
          </Field>
          <Field label="Notes" hint="Optional. Anything the next person should know.">
            {(props) => (
              <Input
                {...props}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" loading={open.isPending}>
            Open the shift
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

function OpenShift({ shiftId, onClosed }: { shiftId: string; onClosed: () => void }) {
  const summary = useQuery({
    queryKey: ['pos', 'shift', shiftId, 'summary'],
    queryFn: () => getShiftSummary(shiftId),
    // The takings move with every sale, so this is never stale-cached: a cashier counting
    // the drawer against a figure from twenty minutes ago is counting against nothing.
    staleTime: 0,
  })

  const sales = useQuery({
    queryKey: ['pos', 'shift', shiftId, 'sales'],
    queryFn: () => listSales({ shift_id: shiftId }),
    staleTime: 0,
  })

  return (
    <div className="space-y-4">
      <QueryState isLoading={summary.isLoading} error={summary.error}>
        {summary.data && <Takings summary={summary.data} />}
      </QueryState>

      {summary.data && (
        <CloseTheDrawer summary={summary.data} shiftId={shiftId} onClosed={onClosed} />
      )}

      <Card>
        <CardHeader title="Sales this shift" description="Click one to reprint its receipt." />
        <QueryState
          isLoading={sales.isLoading}
          error={sales.error}
          isEmpty={(sales.data?.data.length ?? 0) === 0}
          emptyMessage="Nothing has been sold on this shift yet."
        >
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(sales.data?.data ?? []).map((sale) => (
              <li key={sale.id}>
                <Link
                  to={`/pos/sales/${sale.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  <span className="flex items-center gap-2">
                    <ReceiptText className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    <span>
                      <span className="block font-medium text-slate-900 dark:text-slate-100">
                        {sale.receipt_number}
                      </span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {new Date(sale.completed_at).toLocaleTimeString()}
                        {sale.status === 'voided' && ' · voided'}
                      </span>
                    </span>
                  </span>
                  <span
                    className={
                      sale.status === 'voided'
                        ? 'tabular-nums text-slate-400 line-through'
                        : 'tabular-nums font-medium'
                    }
                  >
                    {formatMoney(sale.total)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  )
}

function Takings({ summary }: { summary: ShiftSummary }) {
  return (
    <Card>
      <CardHeader
        title="Takings so far"
        description="Worked out from the sales, not from a running total kept on the shift."
      />
      <CardBody className="grid gap-4 sm:grid-cols-2">
        <dl className="space-y-1.5 text-sm">
          <Row label="Sales" value={String(summary.transaction_count)} />
          {summary.voided_count > 0 && (
            <Row label="Voided" value={String(summary.voided_count)} />
          )}
          <Row label="Gross" value={formatMoney(summary.gross_sales)} />
          <Row label="Discounts" value={formatMoney(summary.discount_amount)} />
          <Row label="Tax" value={formatMoney(summary.tax_amount)} />
          <Row label="Net" value={formatMoney(summary.net_sales)} />
        </dl>
        <dl className="space-y-1.5 text-sm">
          <Row label="Cash" value={formatMoney(summary.cash_takings)} />
          <Row label="Card" value={formatMoney(summary.card_takings)} />
          <Row label="Store credit" value={formatMoney(summary.store_credit_takings)} />
          <Row label="Opening float" value={formatMoney(summary.opening_float)} />
          <Row label="Expected in drawer" value={formatMoney(summary.expected_cash)} strong />
        </dl>
      </CardBody>
    </Card>
  )
}

function CloseTheDrawer({
  summary,
  shiftId,
  onClosed,
}: {
  summary: ShiftSummary
  shiftId: string
  onClosed: () => void
}) {
  const [counted, setCounted] = useState('')
  const [notes, setNotes] = useState('')
  const [closed, setClosed] = useState<ShiftSummary | null>(null)

  const variance = useMemo(
    () => (counted === '' ? null : toCents(counted) - toCents(summary.expected_cash)),
    [counted, summary.expected_cash],
  )

  const close = useMutation({
    mutationFn: () =>
      closeShift(shiftId, {
        closing_cash: fromCents(toCents(counted)),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (result) => {
      setClosed(result)
      toast.success('Shift closed.')
      onClosed()
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not close the shift.'),
  })

  if (closed) {
    return (
      <Alert
        tone={Number(closed.variance ?? 0) === 0 ? 'success' : 'info'}
        title={
          Number(closed.variance ?? 0) === 0
            ? 'The drawer balanced.'
            : `The drawer is out by ${formatMoney(closed.variance)}.`
        }
      >
        Counted {formatMoney(closed.counted_cash)} against {formatMoney(closed.expected_cash)}{' '}
        expected. The shift is closed and the figures are on the record.
      </Alert>
    )
  }

  return (
    <Card>
      <CardHeader
        title="Close the shift"
        description="Count the cash first. A shift closed without a count has no variance, which is not the same as one that balanced."
      />
      <CardBody>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            close.mutate()
          }}
        >
          <Field label="Counted cash" hint="Everything in the drawer, float included.">
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                placeholder="0.00"
                className="text-right tabular-nums"
                value={counted}
                onChange={(event) => setCounted(event.target.value)}
              />
            )}
          </Field>

          {variance !== null && (
            <p
              className={
                variance === 0
                  ? 'text-sm text-success'
                  : 'text-sm font-medium text-warning'
              }
            >
              {variance === 0
                ? 'Balances exactly.'
                : variance > 0
                  ? `${formatMoney(fromCents(variance))} more than expected.`
                  : `${formatMoney(fromCents(-variance))} short.`}
            </p>
          )}

          <Field label="Notes" hint="Optional. Worth a line if the drawer is out.">
            {(props) => (
              <Input {...props} value={notes} onChange={(event) => setNotes(event.target.value)} />
            )}
          </Field>

          <Button type="submit" loading={close.isPending} disabled={counted === ''}>
            <LockKeyhole className="h-4 w-4" />
            Close the shift
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

function ShiftHistory({ terminalId }: { terminalId: string }) {
  const shifts = useQuery({
    queryKey: ['pos', 'shifts', terminalId],
    queryFn: () => listShifts({ terminal_id: terminalId, status: 'closed' }),
  })

  return (
    <Card>
      <CardHeader title="Earlier shifts" />
      <QueryState
        isLoading={shifts.isLoading}
        error={shifts.error}
        isEmpty={(shifts.data?.data.length ?? 0) === 0}
        emptyMessage="No shift has been closed on this till yet."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th scope="col" className="px-5 py-2">Opened</th>
                <th scope="col" className="px-5 py-2">Closed</th>
                <th scope="col" className="px-5 py-2 text-right">Expected</th>
                <th scope="col" className="px-5 py-2 text-right">Counted</th>
                <th scope="col" className="px-5 py-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {(shifts.data?.data ?? []).map((shift) => (
                <tr key={shift.id}>
                  <td className="px-5 py-2">{new Date(shift.opened_at).toLocaleString()}</td>
                  <td className="px-5 py-2">
                    {shift.closed_at ? new Date(shift.closed_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {formatMoney(shift.expected_cash)}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {formatMoney(shift.closing_cash)}
                  </td>
                  <td
                    className={
                      Number(shift.variance ?? 0) === 0
                        ? 'px-5 py-2 text-right tabular-nums'
                        : 'px-5 py-2 text-right font-medium tabular-nums text-warning'
                    }
                  >
                    {formatMoney(shift.variance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Card>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={
        strong
          ? 'flex justify-between border-t border-slate-200 pt-1.5 font-semibold text-slate-900 dark:border-slate-800 dark:text-slate-100'
          : 'flex justify-between'
      }
    >
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  )
}
